import {useCallback, useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Avatar, Voice} from '@contentlab/api/avatars';
import useAvatarsQuery from '@contentlab/hooks/use-avatars-query';
import useVoicesQuery from '@contentlab/hooks/use-voices-query';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {AvatarKind} from '@contentlab/types/aiAvatars';
import type {AvatarFilter} from '@contentlab/types/avatarFilters';
import {
  VOICE_ONLY_FILTER_KEYS,
  LIBRARY_FILTER_KEYS,
  createFiltersFromKeys,
} from './avatarAndVoicesFilters';

// Filter function for strict comparison
const filterFunctions = {
  strict: (value: string, selectedOptions: string[]) => {
    return selectedOptions.includes(value);
  },
} as const;

// Common filtering pipeline - extracted shared logic
function applyFilterPipeline<T>(
  item: T,
  selectedFilters: Record<string, string[]>,
  filterKeys: readonly string[],
  getValue: (item: T, key: string) => any
): boolean {
  // Check each active filter
  // eslint-disable-next-line no-restricted-syntax
  for (const filterKey of filterKeys) {
    const selectedOptions = selectedFilters[filterKey];

    // Skip if no options selected for this filter
    if (!selectedOptions || selectedOptions.length === 0) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // Get the value for this filter key
    const itemValue = getValue(item, filterKey);

    // Apply strict comparison for all filters
    if (itemValue && !filterFunctions.strict(itemValue, selectedOptions)) {
      return false;
    }
  }

  return true;
}

// Value getter functions for different item types
const getAvatarValue = (avatar: Avatar, key: string): any => {
  switch (key) {
    case 'presentationStyle':
      return avatar.presentationStyle;
    case 'culturalRepresentation':
      return avatar.culturalRepresentation;
    default:
      return (avatar as any)[key];
  }
};

const getVoiceValue = (voice: Voice, key: string): any => {
  switch (key) {
    case 'useCase':
      return voice.useCase;
    case 'category':
      return voice.category;
    case 'accent':
      return voice.accent;
    default:
      return (voice as any)[key];
  }
};

// Filter pipeline function for avatars
function filterAvatars(
  avatars: Avatar[],
  selectedFilters: Record<string, string[]>
): Avatar[] {
  if (!avatars) {
    return [];
  }

  return avatars.filter((avatar) =>
    applyFilterPipeline(
      avatar,
      selectedFilters,
      LIBRARY_FILTER_KEYS,
      getAvatarValue
    )
  );
}

// Filter pipeline function for voices
function filterVoices(
  voices: Voice[],
  selectedFilters: Record<string, string[]>
): Voice[] {
  if (!voices) {
    return [];
  }

  return voices.filter((voice) =>
    applyFilterPipeline(
      voice,
      selectedFilters,
      VOICE_ONLY_FILTER_KEYS,
      getVoiceValue
    )
  );
}

interface UseAvatarAndVoicesFiltersReturn {
  avatars: Avatar[];
  filteredAvatars: Avatar[];
  voices: Voice[];
  filteredVoices: Voice[];
  avatarFilters: AvatarFilter[];
  onAvatarFilterChange: (filterId: string, selectedOptionIds: string[]) => void;
  isLoading: boolean;
}

function useAvatarAndVoicesFilters(
  avatarKind: AvatarKind
): UseAvatarAndVoicesFiltersReturn {
  const {data: avatars, isLoading: isLoadingAvatars} = useAvatarsQuery();
  const {data: voices, isLoading: isLoadingVoices} = useVoicesQuery();

  const selectedAvatarFilters = contentLabStore(
    useShallow((state) => state.selectedAvatarFilters)
  );
  const selectedVoiceOnlyFilters = contentLabStore(
    useShallow((state) => state.selectedVoiceOnlyFilters)
  );

  const filteredAvatars = useMemo(() => {
    return filterAvatars(avatars || [], selectedAvatarFilters);
  }, [avatars, selectedAvatarFilters]);

  const filteredVoices = useMemo(() => {
    return filterVoices(voices || [], selectedVoiceOnlyFilters);
  }, [voices, selectedVoiceOnlyFilters]);

  const avatarFilters = useMemo(() => {
    let baseFilters: Omit<AvatarFilter, 'selectedOptionId'>[];

    switch (avatarKind) {
      case AvatarKind.VOICE_ONLY:
        baseFilters = createFiltersFromKeys(VOICE_ONLY_FILTER_KEYS, 'voice');
        break;
      case AvatarKind.LIBRARY:
        baseFilters = createFiltersFromKeys(LIBRARY_FILTER_KEYS, 'avatar');
        break;
      default:
        return [];
    }

    // Get the correct filter state based on avatarKind
    const currentFilters =
      avatarKind === AvatarKind.VOICE_ONLY
        ? selectedVoiceOnlyFilters
        : selectedAvatarFilters;

    // Merge with selected filters from store
    return baseFilters.map((filter) => ({
      ...filter,
      selectedOptionId: currentFilters[filter.id] || [],
    }));
  }, [avatarKind, selectedAvatarFilters, selectedVoiceOnlyFilters]);

  const onAvatarFilterChange = useCallback(
    (filterId: string, selectedOptionIds: string[]) => {
      if (avatarKind === AvatarKind.VOICE_ONLY) {
        contentLabActions.updateSelectedVoiceOnlyFilters(
          filterId,
          selectedOptionIds
        );
      } else {
        contentLabActions.updateSelectedAvatarFilters(
          filterId,
          selectedOptionIds
        );
      }
    },
    [avatarKind]
  );

  const isLoading = isLoadingAvatars || isLoadingVoices;

  return {
    avatars: avatars || [],
    filteredAvatars,
    voices: voices || [],
    filteredVoices,
    avatarFilters,
    onAvatarFilterChange,
    isLoading,
  };
}

export default useAvatarAndVoicesFilters;
